import React, { useState, useEffect, useRef } from 'react';
import * as mainService from '@app/services/mainService';
import StatusBadge from "@pages/FUtil/StatusBadge";

const OrderStatusPopover = ({ orderId, children, placement = 'right' }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [show, setShow] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const popoverRef = useRef(null);
    const triggerRef = useRef(null);

    const fetchLogs = async () => {
        if (!orderId || logs.length > 0) return;

        setLoading(true);
        try {
            const result = await mainService.getLogStatusSO({ id: orderId, name: 'donhang' });
            if (result) setLogs(result);
        } catch (error) {
            console.error('Error fetching logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClick = (e) => {
        e.stopPropagation();
        if (!show) fetchLogs();
        setShow(!show);
    };

    const handleClickOutside = (e) => {
        if (
            popoverRef.current &&
            !popoverRef.current.contains(e.target) &&
            triggerRef.current &&
            !triggerRef.current.contains(e.target)
        ) {
            setShow(false);
        }
    };

    const handleResize = () => {
        setIsMobile(window.innerWidth <= 768);
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (show) {
            document.addEventListener('mousedown', handleClickOutside);
            // Prevent body scroll on mobile when popover is open
            if (isMobile) {
                document.body.style.overflow = 'hidden';
            }
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
            // Restore body scroll
            document.body.style.overflow = '';
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = '';
        };
    }, [show, isMobile]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('vi-VN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getPlacementStyle = () => {
        if (isMobile) {
            // Mobile: Full screen modal style
            return {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                maxWidth: 'none',
                maxHeight: 'none',
                minWidth: 'auto',
                minHeight: 'auto',
                backgroundColor: 'white',
                zIndex: 9999,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            };
        }

        // Desktop logic (unchanged)
        if (!triggerRef.current) return {};

        const rect = triggerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const popoverWidth = 1100;
        const popoverHeight = 550;

        const base = {
            position: 'fixed',
            zIndex: 9999,
            width: `${popoverWidth}px`,
            height: `${popoverHeight}px`,
            maxWidth: 'none',
            maxHeight: 'none',
            minWidth: `${popoverWidth}px`,
            minHeight: `${popoverHeight}px`,
            backgroundColor: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: '12px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
            overflow: 'hidden'
        };

        let left = rect.right + 20;
        let top = rect.top - 20;

        if (left + popoverWidth > viewportWidth - 20) {
            left = rect.left - popoverWidth - 20;
            if (left < 20) {
                left = (viewportWidth - popoverWidth) / 2;
            }
        }

        if (top + popoverHeight > viewportHeight - 20) {
            top = viewportHeight - popoverHeight - 20;
        }

        if (top < 20) {
            top = 20;
        }

        return {
            ...base,
            left: `${Math.max(20, left)}px`,
            top: `${Math.max(20, top)}px`
        };
    };

    const getStatusBadgeStyle = (status) => {
        const statusColors = {
            'Đã gửi KH - Đang giao': { bg: '#fff3cd', color: '#856404', border: '#ffeaa7' },
            'KH đã nhận - hoàn tất đơn': { bg: '#d4edda', color: '#155724', border: '#00b894' },
            'Đã hủy': { bg: '#f8d7da', color: '#721c24', border: '#e17055' },
            'Đang xử lý (điều phối)': { bg: '#f4f4f4', color: '#495057', border: '#b2bec3' }
        };
        const defaultStyle = { bg: '#e3f2fd', color: '#1976d2', border: '#74b9ff' };
        return statusColors[status] || defaultStyle;
    };

    const renderMobileTable = () => (
        <div style={{
            overflowY: 'auto',
            overflowX: 'hidden',
            height: '100%',
            padding: '16px'
        }}>
            {logs.map((log, index) => {
                const statusStyle = getStatusBadgeStyle(log.trang_thai);
                return (
                    <div
                        key={index}
                        style={{
                            backgroundColor: 'white',
                            border: '1px solid #e9ecef',
                            borderRadius: '12px',
                            padding: '16px',
                            marginBottom: '12px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
                        }}
                    >
                        {/* Status Badge */}
                        <div style={{ marginBottom: '12px' }}>
                            {/*<span style={{
                                display: 'inline-block',
                                padding: '6px 12px',
                                backgroundColor: statusStyle.bg,
                                color: statusStyle.color,
                                border: `1px solid ${statusStyle.border}`,
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                {log.trang_thai}
                            </span>*/}
                            <StatusBadge status={log.trang_thai} />>
                        </div>

                        {/* Action */}
                        <div style={{ marginBottom: '8px' }}>
                            <div style={{
                                fontSize: '12px',
                                color: '#6c757d',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginBottom: '4px'
                            }}>
                                Thao tác
                            </div>
                            <div style={{
                                fontSize: '14px',
                                color: '#495057',
                                fontWeight: '500',
                                lineHeight: '1.4'
                            }}>
                                {log.thao_tac}
                            </div>
                        </div>

                        {/* Date and User in row */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '16px'
                        }}>
                            {/* Date */}
                            <div style={{ flex: 1 }}>
                                <div style={{
                                    fontSize: '12px',
                                    color: '#6c757d',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    marginBottom: '4px'
                                }}>
                                    Thời gian
                                </div>
                                <div style={{
                                    fontSize: '13px',
                                    color: '#495057'
                                }}>
                                    <div style={{ fontWeight: '500' }}>
                                        {formatDate(log.ngay_tao).split(' ')[0]}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                        {formatDate(log.ngay_tao).split(' ')[1]}
                                    </div>
                                </div>
                            </div>

                            {/* User */}
                            <div style={{ flex: 1 }}>
                                <div style={{
                                    fontSize: '12px',
                                    color: '#6c757d',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    marginBottom: '4px'
                                }}>
                                    Người thực hiện
                                </div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <div style={{
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: '#e9ecef',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        color: '#6c757d'
                                    }}>
                                        {log.nguoi_tao ? log.nguoi_tao.charAt(0).toUpperCase() : 'U'}
                                    </div>
                                    <span style={{
                                        fontSize: '13px',
                                        color: '#495057',
                                        fontWeight: '500'
                                    }}>
                                        {log.nguoi_tao || 'Không xác định'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const renderDesktopTable = () => (
        <div style={{
            overflowY: 'auto',
            overflowX: 'hidden',
            height: '100%'
        }}>
            <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
            }}>
                <thead style={{
                    backgroundColor: '#f8f9fa',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    borderBottom: '2px solid #dee2e6'
                }}>
                <tr>
                    <th style={{
                        padding: '16px 24px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                        width: '15%',
                        fontSize: '13px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        Thao tác
                    </th>
                    <th style={{
                        padding: '16px 24px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                        width: '15%',
                        fontSize: '13px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        Ngày thực hiện
                    </th>
                    <th style={{
                        padding: '16px 24px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                        width: '20%',
                        fontSize: '13px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        Thời gian hoàn thành
                    </th>
                    <th style={{
                        padding: '16px 24px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                        width: '20%',
                        fontSize: '13px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        Người thực hiện
                    </th>
                    <th style={{
                        padding: '16px 24px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                        width: '30%',
                        fontSize: '13px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        Trạng thái
                    </th>
                </tr>
                </thead>
                <tbody>
                {logs.map((log, index) => {
                    const statusStyle = getStatusBadgeStyle(log.trang_thai);
                    return (
                        <tr
                            key={index}
                            style={{
                                borderBottom: '1px solid #f0f0f0',
                                backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa'
                            }}
                        >
                            <td style={{
                                padding: '16px 24px',
                                verticalAlign: 'middle',
                                color: '#495057',
                                fontSize: '14px',
                                lineHeight: '1.4'
                            }}>
                                <div style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontWeight: '500'
                                }} title={log.thao_tac}>
                                    {log.thao_tac}
                                </div>
                            </td>
                            <td style={{
                                padding: '16px 24px',
                                verticalAlign: 'middle',
                                color: '#6c757d',
                                fontSize: '13px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '2px'
                                }}>
                                        <span style={{ fontWeight: '500', color: '#495057' }}>
                                            {formatDate(log.ngay_tao).split(' ')[0]}
                                        </span>
                                    <span style={{ fontSize: '12px' }}>
                                            {formatDate(log.ngay_tao).split(' ')[1]}
                                        </span>
                                </div>
                            </td>

                            {/*<td style={{
                                padding: '16px 24px',
                                verticalAlign: 'middle',
                                color: '#495057',
                                fontSize: '14px',
                                lineHeight: '1.4'
                            }}>
                                <div style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontWeight: '500'
                                }} title={log.thoi_gian_hoan_thanh}>
                                    {log.thoi_gian_hoan_thanh}
                                </div>
                            </td>*/}


                            <td style={{
                                padding: '16px 24px',
                                verticalAlign: 'middle',
                                fontSize: '14px',
                                lineHeight: '1.4'
                            }}>
                                {!['0', 0, '00:00:00'].includes(log.thoi_gian_hoan_thanh) && (
                                    <div style={{
                                        padding: '6px 12px',
                                        borderRadius: '20px',
                                        background: 'linear-gradient(45deg, #17a2b8, #007bff)',
                                        color: '#fff',
                                        fontWeight: '600',
                                        textAlign: 'center',
                                        fontSize: '12px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                                        border: '2px solid rgba(255,255,255,0.3)',
                                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer'
                                    }}
                                         title={log.thoi_gian_hoan_thanh}
                                         onMouseEnter={(e) => {
                                             e.target.style.transform = 'scale(1.05)';
                                             e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)';
                                         }}
                                         onMouseLeave={(e) => {
                                             e.target.style.transform = 'scale(1)';
                                             e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)';
                                         }}>
                                        {`⏰ ${log.thoi_gian_hoan_thanh.toString().substring(0,8)}`}
                                    </div>
                                )}
                            </td>

                            <td style={{
                                padding: '16px 24px',
                                verticalAlign: 'middle',
                                color: '#495057',
                                fontSize: '14px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <div style={{
                                        width: '32px',
                                        height: '32px',
                                        backgroundColor: '#e9ecef',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        color: '#6c757d'
                                    }}>
                                        {log.nguoi_tao ? log.nguoi_tao.charAt(0).toUpperCase() : 'U'}
                                    </div>
                                    <span style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontWeight: '500'
                                    }} title={log.nguoi_tao}>
                                            {log.nguoi_tao || 'Không xác định'}
                                        </span>
                                </div>
                            </td>
                            <td style={{
                                padding: '16px 24px',
                                verticalAlign: 'middle'
                            }}>
                                    <span style={{
                                        display: 'inline-block',
                                        padding: '6px 12px',
                                        backgroundColor: statusStyle.bg,
                                        color: statusStyle.color,
                                        border: `1px solid ${statusStyle.border}`,
                                        borderRadius: '20px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }} title={log.trang_thai}>
                                        {log.trang_thai}
                                    </span>
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
        </div>
    );

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <span
                ref={triggerRef}
                onClick={handleClick}
                style={{
                    cursor: 'pointer'
                }}
                className="log-trigger"
                title="Click để xem lịch sử trạng thái đơn hàng"
            >
                {children}
            </span>

            {show && (
                <div
                    ref={popoverRef}
                    style={getPlacementStyle()}
                    className="popover"
                >
                    {/* Header */}
                    <div style={{
                        padding: isMobile ? '16px 20px' : '20px 24px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        borderTopLeftRadius: isMobile ? '0' : '11px',
                        borderTopRightRadius: isMobile ? '0' : '11px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexShrink: 0,
                        position: 'relative'
                    }}>
                        {/* Close button - Left side */}
                        <button
                            onClick={() => setShow(false)}
                            style={{
                                background: 'rgba(255,255,255,0.2)',
                                border: 'none',
                                borderRadius: '50%',
                                width: isMobile ? '32px' : '36px',
                                height: isMobile ? '32px' : '36px',
                                cursor: 'pointer',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: isMobile ? '18px' : '20px',
                                fontWeight: 'bold'
                            }}
                            title="Đóng"
                        >
                            ×
                        </button>

                        {/* Center content */}
                        <div style={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            <div style={{
                                width: isMobile ? '36px' : '40px',
                                height: isMobile ? '36px' : '40px',
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <i className="fas fa-history" style={{ fontSize: isMobile ? '16px' : '18px' }}></i>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <h5 style={{
                                    margin: 0,
                                    fontSize: isMobile ? '16px' : '18px',
                                    fontWeight: '600',
                                    textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                }}>
                                    Lịch sử đơn hàng
                                </h5>
                                <p style={{
                                    margin: 0,
                                    fontSize: isMobile ? '13px' : '14px',
                                    opacity: 0.9,
                                    fontWeight: '500'
                                }}>
                                    Mã đơn: #{orderId}
                                </p>
                            </div>
                        </div>

                        {/* Empty space for balance */}
                        <div style={{
                            width: isMobile ? '32px' : '36px',
                            height: isMobile ? '32px' : '36px'
                        }}></div>
                    </div>

                    {/* Body */}
                    <div style={{
                        flex: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        height: isMobile ? 'calc(100% - 140px)' : 'calc(100% - 140px)',
                        backgroundColor: isMobile ? '#f8f9fa' : 'white'
                    }}>
                        {loading ? (
                            <div style={{
                                padding: isMobile ? '40px 20px' : '60px 24px',
                                textAlign: 'center',
                                backgroundColor: '#fafafa'
                            }}>
                                <div style={{
                                    display: 'inline-block',
                                    width: isMobile ? '32px' : '40px',
                                    height: isMobile ? '32px' : '40px',
                                    border: '4px solid #f3f3f3',
                                    borderTop: '4px solid #667eea',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    marginBottom: '16px'
                                }}></div>
                                <p style={{
                                    color: '#6c757d',
                                    fontSize: isMobile ? '14px' : '16px',
                                    margin: 0
                                }}>
                                    Đang tải dữ liệu...
                                </p>
                                <style dangerouslySetInnerHTML={{
                                    __html: `
                                        @keyframes spin {
                                            0% { transform: rotate(0deg); }
                                            100% { transform: rotate(360deg); }
                                        }
                                    `
                                }} />
                            </div>
                        ) : logs.length === 0 ? (
                            <div style={{
                                padding: isMobile ? '40px 20px' : '60px 24px',
                                textAlign: 'center',
                                backgroundColor: '#fafafa'
                            }}>
                                <div style={{
                                    width: isMobile ? '60px' : '80px',
                                    height: isMobile ? '60px' : '80px',
                                    backgroundColor: '#e9ecef',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 20px',
                                    color: '#6c757d'
                                }}>
                                    <i className="fas fa-inbox" style={{ fontSize: isMobile ? '24px' : '32px' }}></i>
                                </div>
                                <h6 style={{
                                    color: '#495057',
                                    marginBottom: '8px',
                                    fontSize: isMobile ? '16px' : '18px'
                                }}>
                                    Chưa có dữ liệu
                                </h6>
                                <p style={{
                                    color: '#6c757d',
                                    margin: 0,
                                    fontSize: isMobile ? '13px' : '14px'
                                }}>
                                    Không tìm thấy lịch sử trạng thái cho đơn hàng này
                                </p>
                            </div>
                        ) : (
                            isMobile ? renderMobileTable() : renderDesktopTable()
                        )}
                    </div>

                    {/* Footer */}
                    {!loading && logs.length > 0 && (
                        <div style={{
                            padding: isMobile ? '12px 20px' : '16px 24px',
                            backgroundColor: '#f8f9fa',
                            borderTop: '1px solid #dee2e6',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: isMobile ? '12px' : '13px',
                            color: '#6c757d',
                            flexShrink: 0
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                    width: '8px',
                                    height: '8px',
                                    backgroundColor: '#28a745',
                                    borderRadius: '50%'
                                }}></div>
                                <span>{isMobile ? 'Real-time' : 'Dữ liệu được cập nhật real-time'}</span>
                            </div>
                            <div style={{ fontWeight: '600', color: '#495057' }}>
                                <i className="fas fa-list-ul" style={{ marginRight: '6px' }}></i>
                                {isMobile ? `${logs.length} bản ghi` : `Tổng cộng: ${logs.length} bản ghi`}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default OrderStatusPopover;